import { type RefObject, useEffect, useRef } from "react";

import { ESCAPE, registerEscape } from "./escapeStack";

/** Ferme un menu ou une fenêtre ouverts : touche Échap et, si `ref` est donné, clic en dehors de l'élément.
 *  Un menu fermé seulement quand le pointeur le quitte restait ouvert si l'on passait directement du bouton
 *  qui l'ouvre à un autre bouton. Échap passe par la pile partagée (escapeStack) : il ne ferme que ce menu, le
 *  plus haut ouvert, et l'appui est consommé (`preventDefault`) — le mode édition de la carte, la sélection ou
 *  le tracé en cours restent en place. `ref` peut être une liste : un clic dans l'un des éléments (bouton qui
 *  ouvre le menu, menu rendu dans un portail) n'est pas « en dehors ». La liste est lue au moment du clic : elle
 *  peut être recréée à chaque rendu sans réinscrire l'écouteur. */
export function useDismiss(
  open: boolean,
  onClose: () => void,
  ref?: RefObject<HTMLElement | null> | readonly RefObject<HTMLElement | null>[],
): void {
  const close = useRef(onClose);
  close.current = onClose;
  const refs = useRef(ref);
  refs.current = ref;
  const hasRef = ref !== undefined;

  // Inscription à l'ouverture seulement : une fonction `onClose` recréée à chaque rendu ne fait pas passer ce
  // menu au-dessus d'un autre ouvert après lui.
  useEffect(() => {
    if (!open) return;
    return registerEscape(ESCAPE.menu, () => close.current());
  }, [open]);

  useEffect(() => {
    if (!open || !hasRef) return;
    const onDown = (e: PointerEvent) => {
      const current = refs.current;
      if (!current) return;
      const list = Array.isArray(current) ? current : [current as RefObject<HTMLElement | null>];
      const target = e.target as Node;
      const elements = list.map((r) => r.current).filter((el): el is HTMLElement => Boolean(el));
      if (elements.length && !elements.some((el) => el.contains(target))) close.current();
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [open, hasRef]);
}
